import { useState, useEffect } from "react";
import { Card, Col, Row, Statistic, Typography } from "antd";

import {
  AreaChart,
  Area,
  Tooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { useNavigate } from "react-router-dom";
import useQuery from "../../../../../hooks/useQuery";
import { useContext } from "react";
import { UserContext } from "../../../../../context/UserContext";
import dayjs from "dayjs";

const CommonGraph = ({
  title,
  data = [],
  lineColor,
  activeX,
  onPointChange,
  mobileCompact = false,
}) => {
  const { userDetails } = useContext(UserContext);
  const role = userDetails?.currRole?.toLowerCase()?.trim();

  const navigate = useNavigate();
  const params = useQuery();
  const modifiedData = [{ name: 0, value: 0 }, ...data];

  const [displayValue, setDisplayValue] = useState(
    modifiedData?.[modifiedData?.length - 1]?.value
  );
  useEffect(() => {
    if (activeX !== null) {
      const match = modifiedData[activeX];
      if (match) setDisplayValue(match.value);
    }
  }, [activeX]);

  const formatTo12Hr = (time) => {
    if (!time) return "";
    const [hourStr, minuteStr] = time.split(":");
    const hour = parseInt(hourStr);
    const minute = minuteStr;
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${hour12}:${minute} ${ampm}`;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const value = payload[0].payload.value;
      const datetime = payload[0].payload.datetime;
      setDisplayValue(value);

      // ✅ After — keeps the time as UTC (no conversion)
      const time = datetime ? dayjs.utc(datetime).format("hh:mm A") : "";
      const date = datetime ? dayjs.utc(datetime).format("MMM DD, YYYY") : "";

      return (
        <div
          style={{
            background: "#fff",
            border: "1px solid #e8e8e8",
            borderRadius: "6px",
            padding: "6px 10px",
            fontSize: "12px",
            color: "#333",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <div style={{ fontWeight: 600 }}>{date}</div>
          <div style={{ color: "#888" }}>{time}</div>
        </div>
      );
    }
    return null;
  };

  const handleMouseMove = (state) => {
    if (state?.activeTooltipIndex !== undefined) {
      onPointChange(state.activeTooltipIndex);
    }
  };

  return (
    <Card
      size="small"
      className={`tiny-graph-card${mobileCompact ? " vital-graph-mobile" : ""}`}
      style={{ height: "100%" }}
      title={
        mobileCompact ? (
          <div className="vital-graph-mobile-header">
            <Typography
              onClick={() => {
                if (role?.includes("nurse")) {
                  navigate(
                    `/nurse/home?status=${params?.status}&patient=${params?.patient}&type=monitoring-charts`
                  );
                } else {
                  navigate(
                    `/doctor/home?patient-type=${params?.["patient-type"]}&status=${params?.status}&patient=${params?.patient}&type=monitoring-charts`
                  );
                }
              }}
              style={{ cursor: "pointer" }}
            >
              {title}
            </Typography>
            <Statistic
              value={displayValue}
              valueStyle={{
                color: "#52C41A",
                fontWeight: 600,
                fontSize: "22px",
              }}
            />
          </div>
        ) : (
          <Row>
            <Col span={12}>
              <Typography
                onClick={() => {
                  if (role?.includes("nurse")) {
                    navigate(
                      `/nurse/home?status=${params?.status}&patient=${params?.patient}&type=monitoring-charts`
                    );
                  } else {
                    navigate(
                      `/doctor/home?patient-type=${params?.["patient-type"]}&status=${params?.status}&patient=${params?.patient}&type=monitoring-charts`
                    );
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                {title}
              </Typography>
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <Statistic
                value={displayValue}
                valueStyle={{
                  color: "#52C41A",
                  fontWeight: 600,
                  fontSize: "24px",
                }}
              />
            </Col>
          </Row>
        )
      }
    >
      <ResponsiveContainer
        width="100%"
        height={mobileCompact ? 88 : 100}
        className="line-chart"
      >
        {modifiedData?.length === 1 ? (
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <p style={{ color: "#b3b3b3" }}>No Data</p>
          </div>
        ) : (
          <AreaChart
            data={modifiedData}
            margin={
              mobileCompact
                ? { top: 6, right: 6, left: 2, bottom: 2 }
                : { top: 8, right: 8, left: 0, bottom: 0 }
            }
            onMouseMove={handleMouseMove}
            onClick={handleMouseMove}
          >
            <XAxis dataKey="name" hide={true} />
            <YAxis
              domain={["dataMin - 1", "dataMax + 1"]}
              hide={true}
              width={0}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={lineColor}
              fill={lineColor}
              fillOpacity={0.2}
              strokeWidth={2}
              activeDot={false}
              isAnimationActive={false}
              dot={(props) => {
                if (props.index === activeX) {
                  return (
                    <circle
                      cx={props.cx}
                      cy={props.cy}
                      r={4}
                      fill={lineColor}
                      stroke="#fff"
                      strokeWidth={2}
                    />
                  );
                }
                return null;
              }}
            />

            <Tooltip content={<CustomTooltip />} cursor={false} />
          </AreaChart>
        )}
      </ResponsiveContainer>
    </Card>
  );
};

export default CommonGraph;
